import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import symbol from "../assets/senesight-symbol-ondark.svg";
import { useSession } from "./session.js";
import { isRole } from "../roles.js";
import {
  completeEmailSignInLink,
  firebaseConfigured,
  forgetRememberedEmail,
  isEmailSignInLink,
  readRememberedEmail,
  rememberEmailForLink,
  sendEmailSignInLink,
  signInWithGoogle,
  signInWithMicrosoft,
  type ExchangedSession,
} from "./firebase.js";

/**
 * Sign-in page (D7). SeneSight authenticates via the SeneTek Portal Firebase SSO project: the user
 * signs in with an enabled provider, and the resulting Firebase ID token is exchanged at
 * /auth/session for a short-lived SeneSight session (the API verifies the token and resolves membership).
 * The page holds no secret and never mints a session itself.
 */
const methods = {
  google: { label: "Sign in with Google", signIn: signInWithGoogle },
  microsoft: { label: "Sign in with Microsoft", signIn: signInWithMicrosoft },
} as const;
type SignInMethod = keyof typeof methods | "email-link";
const supportedMethods: readonly string[] = [...Object.keys(methods), "email-link"];
const EMAIL_CONFIRMATION = "Check your email for the sign-in link";

function hasAuthCode(error: unknown, code: string): boolean {
  return error instanceof Error && "code" in error && error.code === code;
}

function enabledMethods(): SignInMethod[] {
  const raw = import.meta.env.VITE_SIGN_IN_METHODS as string | undefined;
  const configured = (raw ?? "")
    .split(",")
    .map((value: string) => value.trim())
    .filter(Boolean);
  const unknown = configured.filter((value) => !supportedMethods.includes(value));
  if (import.meta.env.DEV && unknown.length > 0) {
    console.warn(`Unknown sign-in method(s): ${unknown.join(", ")}`);
  }
  const enabled = [
    ...new Set(
      configured.filter((value): value is SignInMethod => supportedMethods.includes(value)),
    ),
  ];
  return enabled.length > 0 ? enabled : ["google"];
}

export function Login(): React.ReactNode {
  const location = useLocation() as Location & { state: { from?: string } | null };
  const from = location.state?.from ?? "/portfolio";
  const navigate = useNavigate();
  const { signIn } = useSession();
  const enabled = enabledMethods();
  const emailEnabled = enabled.includes("email-link");
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [confirmation, setConfirmation] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [busyMethod, setBusyMethod] = useState<SignInMethod | null>(null);
  const checkedLink = useRef(false);

  const finishSignIn = (s: ExchangedSession, destination: string): void => {
    if (!isRole(s.role)) throw new Error(`account has an unrecognized role "${s.role}"`);
    signIn({ subject: s.subject, tenant: s.tenant, role: s.role, token: s.token });
    navigate(destination, { replace: true });
  };

  const completeLink = async (address: string, url: string): Promise<void> => {
    setError(null);
    setBusyMethod("email-link");
    try {
      const s = await completeEmailSignInLink(address, url);
      forgetRememberedEmail();
      finishSignIn(s, "/portfolio");
    } catch (e) {
      if (
        hasAuthCode(e, "auth/invalid-action-code") ||
        hasAuthCode(e, "auth/expired-action-code")
      ) {
        forgetRememberedEmail();
        setLinkUrl(null);
        setConfirmation(false);
        setEmail("");
        setError("This sign-in link has expired. Request a new one.");
      } else {
        setError(e instanceof Error ? e.message : "sign-in failed");
      }
    } finally {
      setBusyMethod(null);
    }
  };

  useEffect(() => {
    if (checkedLink.current || !emailEnabled || !firebaseConfigured()) return;
    checkedLink.current = true;
    const url = window.location.href;
    try {
      if (!isEmailSignInLink(url)) return;
      setLinkUrl(url);
      const remembered = readRememberedEmail();
      if (remembered) {
        setEmail(remembered);
        void completeLink(remembered, url);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "sign-in failed");
    }
  }, [emailEnabled]);

  const onSignIn = async (method: keyof typeof methods): Promise<void> => {
    setError(null);
    setBusyMethod(method);
    try {
      const s = await methods[method].signIn();
      finishSignIn(s, from);
    } catch (e) {
      setError(e instanceof Error ? e.message : "sign-in failed");
    } finally {
      setBusyMethod(null);
    }
  };

  const onEmailSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setError("Enter a valid email address.");
      return;
    }
    if (linkUrl) {
      await completeLink(address, linkUrl);
      return;
    }
    setBusyMethod("email-link");
    try {
      await sendEmailSignInLink(address);
      rememberEmailForLink(address);
      setConfirmation(true);
    } catch (e) {
      // A provider configured to report unknown accounts must not disclose that fact here.
      if (hasAuthCode(e, "auth/user-not-found")) {
        setConfirmation(true);
      } else {
        setError(e instanceof Error ? e.message : "sign-in failed");
      }
    } finally {
      setBusyMethod(null);
    }
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: "var(--sl-space-lg, 24px)",
      }}
    >
      <section className="panel pad" style={{ maxWidth: 380, width: "100%", textAlign: "center" }}>
        <img src={symbol} alt="SeneSight" width={40} height={40} style={{ marginBottom: 14 }} />
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>SeneSight</h1>
        <p className="lab" style={{ marginBottom: 22 }}>
          Delivery Intelligence
        </p>
        {enabled
          .filter((method): method is keyof typeof methods => method !== "email-link")
          .map((method, index) => (
            <button
              key={method}
              type="button"
              className="btn"
              style={{ width: "100%", marginTop: index ? "var(--sl-space-lg, 24px)" : undefined }}
              onClick={() => void onSignIn(method)}
              disabled={busyMethod !== null || !firebaseConfigured()}
            >
              {busyMethod === method ? "Signing in…" : methods[method].label}
            </button>
          ))}
        {emailEnabled && (
          <form onSubmit={(event) => void onEmailSubmit(event)} noValidate>
            {!confirmation && (
              <>
                {linkUrl && <p className="lab">Enter your email address to finish signing in.</p>}
                <div className="field" style={{ marginTop: "var(--sl-space-lg, 24px)" }}>
                  <label className="field-label" htmlFor="sign-in-email">
                    Email address
                  </label>
                  <input
                    id="sign-in-email"
                    className="input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={busyMethod !== null || !firebaseConfigured()}
                  />
                </div>
                <button
                  type="submit"
                  className="btn"
                  style={{ width: "100%", marginTop: "var(--sl-space-lg, 24px)" }}
                  disabled={busyMethod !== null || !firebaseConfigured()}
                >
                  {linkUrl ? "Complete sign-in" : "Email me a sign-in link"}
                </button>
              </>
            )}
            {confirmation && <p role="status">{EMAIL_CONFIRMATION}</p>}
          </form>
        )}
        {!firebaseConfigured() && (
          <p style={{ color: "var(--neg)", fontSize: 12, marginTop: 12 }}>
            {import.meta.env.DEV
              ? // Developer-facing hint — only in dev builds. The internal doc path (RS.0.19) must
                // NEVER render to public prod users; a prod build that lost VITE_FIREBASE_* once
                // leaked it. In prod the fallback is a generic, non-internal message.
                "Sign-in is not configured (VITE_FIREBASE_* missing). See docs/DOGFOOD-LOCAL.md."
              : "Sign-in is temporarily unavailable. Please contact your administrator."}
          </p>
        )}
        {error && <p style={{ color: "var(--neg)", fontSize: 12, marginTop: 12 }}>{error}</p>}
        <p style={{ color: "var(--faint)", fontSize: 12, marginTop: 16 }}>
          Evidence-backed delivery intelligence. Every figure shows its source and confidence.
        </p>
      </section>
    </main>
  );
}
